import React from 'react';
import { formatNumber } from '../utils/formatters';
import type { ChangeMetric } from '../types';
import './StatCard.css';

export type StatTone = 'neutral' | 'brand' | 'success' | 'warning' | 'error';

/** The signal of a change: good shows green, bad shows red, flat shows grey. */
export type StatSignal = 'good' | 'bad' | 'flat';

export interface StatDetail {
  label: string;
  value: React.ReactNode;
}

interface StatCardProps {
  label: string;
  /** A number gets the short format of formatNumber (12.3K). Leave it out for a details-only card. */
  value?: React.ReactNode;
  suffix?: string;
  icon?: string;
  tone?: StatTone;
  /** The change against the period before. The API gives `change` in percent. */
  change?: ChangeMetric | null;
  changeLabel?: string;
  /** Set it when a rise is bad news, such as pending bookings or reports. */
  riseIsBad?: boolean;
  /** One short line under the value. */
  hint?: React.ReactNode;
  /** A small tag in the corner, such as a health status. */
  badge?: React.ReactNode;
  details?: StatDetail[];
  loading?: boolean;
}

export const changeSignal = (change: number, riseIsBad = false): StatSignal => {
  if (change === 0) return 'flat';
  return (change > 0) !== riseIsBad ? 'good' : 'bad';
};

const ChangeLine: React.FC<{ change: ChangeMetric; label: string; riseIsBad: boolean }> = ({
  change,
  label,
  riseIsBad,
}) => {
  if (change.change === null || change.change === undefined || Number.isNaN(Number(change.change))) {
    return <div className="sc-change">Няма данни за промяна</div>;
  }
  const pct = Number(change.change);
  const arrow = pct > 0 ? '▲' : pct < 0 ? '▼' : '•';
  return (
    <div className="sc-change">
      <span className="sc-change-pill" data-signal={changeSignal(pct, riseIsBad)}>
        {arrow} {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
      </span>
      <span className="sc-change-label">{label}</span>
    </div>
  );
};

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  suffix = '',
  icon,
  tone = 'neutral',
  change,
  changeLabel = 'от вчера',
  riseIsBad = false,
  hint,
  badge,
  details,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="sc-card" aria-busy="true">
        <div className="sc-skeleton sc-skeleton-label" />
        <div className="sc-skeleton sc-skeleton-value" />
      </div>
    );
  }

  return (
    <div className="sc-card" data-tone={tone} role="group" aria-label={label}>
      <div className="sc-head">
        {icon && <i className={`sc-icon ${icon}`} aria-hidden="true" />}
        <span className="sc-label">{label}</span>
        {badge && <span className="sc-badge">{badge}</span>}
      </div>
      {value !== undefined && value !== null && (
        <div className="sc-value">
          {typeof value === 'number' ? formatNumber(value) : value}
          {suffix}
        </div>
      )}
      {change && <ChangeLine change={change} label={changeLabel} riseIsBad={riseIsBad} />}
      {hint && <div className="sc-hint">{hint}</div>}
      {details && details.length > 0 && (
        <dl className="sc-details">
          {details.map((d) => (
            <div key={d.label} className="sc-detail">
              <dt>{d.label}</dt>
              <dd>{d.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

/** The grid for stat cards. Cards are compact, so a row holds 4 to 6 of them. */
export const StatGrid: React.FC<{ children: React.ReactNode; wide?: boolean }> = ({ children, wide }) => (
  <div className={`sc-grid${wide ? ' sc-grid-wide' : ''}`}>{children}</div>
);

export default StatCard;
